import type { DatasetResource } from "@prequel/import-react";

import type { RecordResource } from "@/api/import/datasets";

/** How the dataset reads from its source, with the concrete config value alongside the method. */
export const getDatasetMethodSummary = (dataset: DatasetResource): string => {
  switch (dataset.method) {
    case "table":
      return `table: ${dataset.table.source_table_namespace}.${dataset.table.source_table_name}`;
    case "sql":
      return `sql: ${dataset.sql.query}`;
    case "glob":
      return `glob: ${dataset.glob.pattern}`;
  }
};

export type RecordStageStatus = "success" | "errored" | "deleted" | "pending";

export type RecordStage = {
  key: string;
  label: string;
  status: RecordStageStatus;
  at?: string;
  id?: string;
  errorCode?: string;
  errorTrace?: string;
};

type RawStage = {
  error?: { error_code?: string; error_trace?: string };
  is_deleted?: boolean;
};

const stageStatus = (stage: RawStage | undefined, at: string | undefined): RecordStageStatus => {
  // Stages serialize their error as a value struct, so a healthy stage still carries `error: {}`.
  if (stage?.error?.error_code || stage?.error?.error_trace) {
    return "errored";
  }
  if (stage?.is_deleted) {
    return "deleted";
  }
  return at ? "success" : "pending";
};

/** The record's pipeline stages in run order, so a reader can see where it stalled. */
export const getRecordStages = (record: RecordResource): RecordStage[] => {
  const raw = [
    {
      key: "extract",
      label: "Extract",
      stage: record.extract,
      at: record.extract?.last_extracted_at,
      id: record.extract?.extract_id,
    },
    {
      key: "transform",
      label: "Transform",
      stage: record.transform,
      at: record.transform?.last_transformed_at,
      id: record.transform?.transform_id,
    },
    {
      key: "validate_schema",
      label: "Validate schema",
      stage: record.validate_schema,
      at: record.validate_schema?.last_validated_at,
    },
    { key: "load", label: "Load", stage: record.load, at: record.load?.last_loaded_at, id: record.load?.load_id },
    {
      key: "acknowledge",
      label: "Acknowledge",
      stage: record.acknowledge,
      at: record.acknowledge?.last_acknowledged_at,
      id: record.acknowledge?.acknowledge_id,
    },
  ];

  return raw.map(({ key, label, stage, at, id }) => ({
    key,
    label,
    status: stageStatus(stage, at),
    at,
    id,
    errorCode: stage?.error?.error_code,
    errorTrace: stage?.error?.error_trace,
  }));
};
